import * as THREE from 'three'
import { OrbitControls as ThreeOrbitControls } from 'three/addons/controls/OrbitControls.js'
import { CONTROL_DAMPING_FACTOR } from '@/shared/constants'
import {
  type NativeControlServices,
  syncRigFromCamera,
  syncViewportCameraFromRig,
} from './NativeControlUtils'

const ORBIT_WORLD_UP = new THREE.Vector3(0, 0, 1)
const ORBIT_SPHERICAL_UP = new THREE.Vector3(0, 1, 0)
const DEFAULT_ELASTIC_RESISTANCE = 0.35
const DEFAULT_ELASTIC_RETURN_RATE = 12
const DEFAULT_ELASTIC_WHEEL_IDLE_MS = 120
// 高于 CameraRig 状态同步容差，避免 on-demand 回弹在目标附近反复被旧状态覆盖。
const ELASTIC_SNAP_EPSILON = 1e-5

/** 一个带有限越界空间的 Orbit 软范围。 */
export interface OrbitElasticRange {
  /** 静止软下限。角度范围使用弧度。 */
  min: number
  /** 静止软上限。角度范围使用弧度。 */
  max: number
  /** 两端软边界外允许继续移动的兼容回退距离。 */
  overscroll?: number
  /** 软下限外允许继续移动的最大距离，优先于 overscroll。 */
  minOverscroll?: number
  /** 软上限外允许继续移动的最大距离，优先于 overscroll。 */
  maxOverscroll?: number
}

/** OrbitController 的弹性角度与缩放边界。 */
export interface OrbitElasticLimits {
  /** 极角软范围，单位为弧度，必须位于 `[0, π]`。 */
  polar?: OrbitElasticRange
  /** 方位角软范围，单位为弧度，首版不支持跨越 `-π/π` 接缝。 */
  azimuth?: OrbitElasticRange
  /** 透视相机 camera-target distance 软范围，单位为世界单位。 */
  distance?: OrbitElasticRange
  /** 正交相机 zoom 软范围。 */
  zoom?: OrbitElasticRange
  /** 越过软边界后保留的输入比例，范围为 `(0, 1]`，默认 `0.35`。 */
  resistance?: number
  /** 输入结束后的指数回弹速率，单位为每秒，默认 `12`。 */
  returnRate?: number
  /** 最后一次滚轮事件后开始回弹的空闲时间，单位为毫秒，默认 `120`。 */
  wheelIdleMs?: number
}

/** OrbitController 可由 ControlPlugin 复用的设置。 */
export interface OrbitControllerSettings {
  /** 可选弹性边界；省略时完整保留原生 OrbitControls 行为。 */
  elasticLimits?: OrbitElasticLimits
}

/** OrbitController options. */
export interface OrbitControllerOptions extends OrbitControllerSettings {
  /** Shared services used by the controller. */
  services: NativeControlServices
}

interface ResolvedElasticRange {
  min: number
  max: number
  minOverscroll: number
  maxOverscroll: number
  hardMin: number
  hardMax: number
}

interface ResolvedElasticLimits {
  polar?: ResolvedElasticRange
  azimuth?: ResolvedElasticRange
  distance?: ResolvedElasticRange
  zoom?: ResolvedElasticRange
  resistance: number
  returnRate: number
  wheelIdleSeconds: number
}

interface OrbitNativeHardLimits {
  minPolarAngle: number
  maxPolarAngle: number
  minAzimuthAngle: number
  maxAzimuthAngle: number
  minDistance: number
  maxDistance: number
  minZoom: number
  maxZoom: number
}

interface OrbitElasticSnapshot {
  polar: number
  azimuth: number
  distance: number
  zoom: number
}

function assertFinite(name: string, value: number): void {
  if (!Number.isFinite(value)) {
    throw new Error(`OrbitController: ${name} 必须是有限数值`)
  }
}

function resolveElasticRange(
  name: string,
  range: OrbitElasticRange | undefined,
  domainMin: number,
  domainMax: number
): ResolvedElasticRange | undefined {
  if (!range) return undefined

  assertFinite(`${name}.min`, range.min)
  assertFinite(`${name}.max`, range.max)
  if (range.min >= range.max) {
    throw new Error(`OrbitController: ${name}.min 必须小于 ${name}.max`)
  }
  if (range.min < domainMin || range.max > domainMax) {
    throw new Error(`OrbitController: ${name} 必须位于 [${domainMin}, ${domainMax}]`)
  }

  if (range.overscroll !== undefined) {
    assertFinite(`${name}.overscroll`, range.overscroll)
    if (range.overscroll < 0) {
      throw new Error(`OrbitController: ${name}.overscroll 不能为负数`)
    }
  }

  const minOverscroll = range.minOverscroll ?? range.overscroll
  const maxOverscroll = range.maxOverscroll ?? range.overscroll
  if (minOverscroll === undefined) {
    throw new Error(`OrbitController: ${name}.minOverscroll 或 ${name}.overscroll 必须提供`)
  }
  if (maxOverscroll === undefined) {
    throw new Error(`OrbitController: ${name}.maxOverscroll 或 ${name}.overscroll 必须提供`)
  }
  assertFinite(`${name}.minOverscroll`, minOverscroll)
  assertFinite(`${name}.maxOverscroll`, maxOverscroll)
  if (minOverscroll < 0) {
    throw new Error(`OrbitController: ${name}.minOverscroll 不能为负数`)
  }
  if (maxOverscroll < 0) {
    throw new Error(`OrbitController: ${name}.maxOverscroll 不能为负数`)
  }

  return {
    min: range.min,
    max: range.max,
    minOverscroll,
    maxOverscroll,
    hardMin: Math.max(domainMin, range.min - minOverscroll),
    hardMax: Math.min(domainMax, range.max + maxOverscroll),
  }
}

function resolveElasticLimits(
  limits: OrbitElasticLimits | undefined
): ResolvedElasticLimits | null {
  if (!limits) return null

  const resistance = limits.resistance ?? DEFAULT_ELASTIC_RESISTANCE
  const returnRate = limits.returnRate ?? DEFAULT_ELASTIC_RETURN_RATE
  const wheelIdleMs = limits.wheelIdleMs ?? DEFAULT_ELASTIC_WHEEL_IDLE_MS
  assertFinite('elasticLimits.resistance', resistance)
  assertFinite('elasticLimits.returnRate', returnRate)
  assertFinite('elasticLimits.wheelIdleMs', wheelIdleMs)
  if (resistance <= 0 || resistance > 1) {
    throw new Error('OrbitController: elasticLimits.resistance 必须位于 (0, 1]')
  }
  if (returnRate <= 0) {
    throw new Error('OrbitController: elasticLimits.returnRate 必须大于 0')
  }
  if (wheelIdleMs < 0) {
    throw new Error('OrbitController: elasticLimits.wheelIdleMs 不能为负数')
  }

  const polar = resolveElasticRange('elasticLimits.polar', limits.polar, 0, Math.PI)
  const azimuth = resolveElasticRange('elasticLimits.azimuth', limits.azimuth, -Math.PI, Math.PI)
  const distance = resolveElasticRange('elasticLimits.distance', limits.distance, 0, Infinity)
  const zoom = resolveElasticRange('elasticLimits.zoom', limits.zoom, Number.MIN_VALUE, Infinity)

  if (!polar && !azimuth && !distance && !zoom) return null

  return {
    polar,
    azimuth,
    distance,
    zoom,
    resistance,
    returnRate,
    wheelIdleSeconds: wheelIdleMs / 1000,
  }
}

function applyOutwardResistance(
  previous: number,
  current: number,
  range: ResolvedElasticRange,
  resistance: number
): number {
  const clamped = THREE.MathUtils.clamp(current, range.hardMin, range.hardMax)
  if (clamped < range.min && clamped < previous) {
    const origin = previous >= range.min ? range.min : previous
    return THREE.MathUtils.clamp(
      origin + (clamped - origin) * resistance,
      range.hardMin,
      range.hardMax
    )
  }
  if (clamped > range.max && clamped > previous) {
    const origin = previous <= range.max ? range.max : previous
    return THREE.MathUtils.clamp(
      origin + (clamped - origin) * resistance,
      range.hardMin,
      range.hardMax
    )
  }
  return clamped
}

function isOutsideSoftRange(value: number, range: ResolvedElasticRange): boolean {
  return value < range.min || value > range.max
}

function returnToSoftRange(
  value: number,
  range: ResolvedElasticRange,
  returnRate: number,
  deltaTime: number
): number {
  const target = THREE.MathUtils.clamp(value, range.min, range.max)
  if (target === value) return value

  const remainingFactor = Math.exp(-returnRate * deltaTime)
  const next = target + (value - target) * remainingFactor
  const snapScale = Math.max(1, Math.abs(target), range.max - range.min)
  return Math.abs(next - target) <= ELASTIC_SNAP_EPSILON * snapScale ? target : next
}

function resolveElasticValue(
  previous: number,
  current: number,
  range: ResolvedElasticRange | undefined,
  limits: ResolvedElasticLimits,
  inputActive: boolean,
  deltaTime: number
): number {
  if (!range) return current

  const resisted = applyOutwardResistance(previous, current, range, limits.resistance)
  if (inputActive) return resisted

  const returnSource = isOutsideSoftRange(previous, range) ? previous : resisted
  return returnToSoftRange(returnSource, range, limits.returnRate, deltaTime)
}

/** Orbit mode powered by Three's official OrbitControls. */
export class OrbitController extends ThreeOrbitControls {
  private services: NativeControlServices | null = null
  private elasticLimits: ResolvedElasticLimits | null = null
  private nativeHardLimits: OrbitNativeHardLimits | null = null
  private elasticEventListenersAttached = false
  private interactionActive = false
  private wheelIdleRemaining = 0
  private elasticDomElement: HTMLElement | null = null
  private readonly elasticQuaternion = new THREE.Quaternion()
  private readonly elasticOffset = new THREE.Vector3()
  private readonly elasticSpherical = new THREE.Spherical()
  private readonly handleInteractionStart = () => {
    this.interactionActive = true
  }
  private readonly handleInteractionEnd = () => {
    this.interactionActive = false
  }
  private readonly handleElasticWheel = () => {
    if (!this.elasticLimits) return
    this.wheelIdleRemaining = this.elasticLimits.wheelIdleSeconds
  }

  constructor(options: OrbitControllerOptions) {
    const camera = syncViewportCameraFromRig(options.services)
    super(camera)

    this.services = options.services
    this.enabled = false
    this.enableDamping = true
    this.dampingFactor = CONTROL_DAMPING_FACTOR
    this.screenSpacePanning = false
    this.target.copy(options.services.rig.target)
    this.setElasticLimits(options.elasticLimits)
  }

  /**
   * Connects the DOM element and syncs camera state.
   *
   * @param element - Element that receives interaction events.
   */
  override connect(element: HTMLElement): void {
    if (!this.services) return
    const camera = this.syncFromRig()
    this.target.copy(this.services.rig.target)
    camera.updateMatrixWorld(true)
    super.connect(element)
    if (this.elasticLimits) {
      this.attachElasticDomListener(element)
    }
    super.update()
  }

  /**
   * Replaces elastic limits without recreating the controller.
   *
   * @param limits - New elastic limits; omit to restore the inherited OrbitControls limits.
   */
  setElasticLimits(limits?: OrbitElasticLimits): void {
    const resolved = resolveElasticLimits(limits)
    const hadElasticLimits = this.elasticLimits !== null

    if (!hadElasticLimits && resolved) {
      this.nativeHardLimits = this.readNativeHardLimits()
    } else if (hadElasticLimits) {
      this.restoreNativeHardLimits()
    }

    this.elasticLimits = resolved
    this.interactionActive = false
    this.wheelIdleRemaining = 0

    if (resolved) {
      this.attachElasticEventListeners()
      if (this.domElement instanceof HTMLElement) {
        this.attachElasticDomListener(this.domElement)
      }
      this.applyConfiguredHardLimits(
        this.object as THREE.PerspectiveCamera | THREE.OrthographicCamera
      )
      return
    }

    this.detachElasticDomListener()
    this.detachElasticEventListeners()
    this.nativeHardLimits = null
  }

  /** Returns whether on-demand rendering must keep advancing elastic motion. */
  hasPendingElasticAnimation(): boolean {
    if (!this.enabled || !this.elasticLimits) return false
    if (this.wheelIdleRemaining > 0) return true

    const snapshot = this.readElasticSnapshot(this.object)
    const { polar, azimuth, distance, zoom } = this.elasticLimits
    return (
      (polar ? isOutsideSoftRange(snapshot.polar, polar) : false) ||
      (azimuth ? isOutsideSoftRange(snapshot.azimuth, azimuth) : false) ||
      (distance && this.object instanceof THREE.PerspectiveCamera
        ? isOutsideSoftRange(snapshot.distance, distance)
        : false) ||
      (zoom && this.object instanceof THREE.OrthographicCamera
        ? isOutsideSoftRange(snapshot.zoom, zoom)
        : false)
    )
  }

  /** Disconnects DOM listeners and resets transient elastic input state. */
  override disconnect(): void {
    this.detachElasticDomListener()
    this.interactionActive = false
    this.wheelIdleRemaining = 0
    if (this.domElement) {
      super.disconnect()
    }
  }

  /**
   * Updates controller state for the current frame.
   *
   * @param deltaTime - Time elapsed since the previous frame, in seconds.
   * @returns true when the visible camera changed.
   */
  override update(deltaTime?: number | null): boolean {
    if (!this.enabled || !this.services) return false

    const camera = this.syncFromRig()
    this.target.copy(this.services.rig.target)
    if (this.services.rig.isTransitioning) {
      camera.updateMatrixWorld(true)
      return false
    }
    this.applyConfiguredHardLimits(camera)
    const frameDelta = this.resolveFrameDelta(deltaTime)
    if (this.wheelIdleRemaining > 0 && deltaTime !== undefined && deltaTime !== null) {
      this.wheelIdleRemaining = Math.max(0, this.wheelIdleRemaining - frameDelta)
    }
    const before = this.elasticLimits ? this.readElasticSnapshot(camera) : null
    const changed = super.update(deltaTime)
    const elasticChanged = before ? this.applyElasticCorrection(camera, before, frameDelta) : false
    camera.updateMatrixWorld(true)
    syncRigFromCamera(this.services, camera, this.target)
    if (elasticChanged) {
      this.dispatchEvent({ type: 'change' })
    }
    return changed || elasticChanged
  }

  /** Returns the current camera polar angle after elastic correction. */
  override getPolarAngle(): number {
    if (!this.elasticLimits) return super.getPolarAngle()
    return this.readElasticSnapshot(this.object).polar
  }

  /** Returns the current camera azimuth after elastic correction. */
  override getAzimuthalAngle(): number {
    if (!this.elasticLimits) return super.getAzimuthalAngle()
    return this.readElasticSnapshot(this.object).azimuth
  }

  syncFromRigWithoutCommit(): void {
    if (!this.services) return
    const camera = this.syncFromRig()
    this.target.copy(this.services.rig.target)
    camera.updateMatrixWorld(true)
  }

  /** Releases controller resources. */
  override dispose(): void {
    this.detachElasticDomListener()
    if (this.domElement) {
      super.dispose()
    }
    this.detachElasticEventListeners()
    this.elasticLimits = null
    this.nativeHardLimits = null
  }

  private attachElasticEventListeners(): void {
    if (this.elasticEventListenersAttached) return
    this.addEventListener('start', this.handleInteractionStart)
    this.addEventListener('end', this.handleInteractionEnd)
    this.elasticEventListenersAttached = true
  }

  private detachElasticEventListeners(): void {
    if (!this.elasticEventListenersAttached) return
    this.removeEventListener('start', this.handleInteractionStart)
    this.removeEventListener('end', this.handleInteractionEnd)
    this.elasticEventListenersAttached = false
  }

  private attachElasticDomListener(element: HTMLElement): void {
    if (this.elasticDomElement === element) return
    this.detachElasticDomListener()
    element.addEventListener('wheel', this.handleElasticWheel)
    this.elasticDomElement = element
  }

  private detachElasticDomListener(): void {
    if (!this.elasticDomElement) return
    this.elasticDomElement.removeEventListener('wheel', this.handleElasticWheel)
    this.elasticDomElement = null
  }

  private readNativeHardLimits(): OrbitNativeHardLimits {
    return {
      minPolarAngle: this.minPolarAngle,
      maxPolarAngle: this.maxPolarAngle,
      minAzimuthAngle: this.minAzimuthAngle,
      maxAzimuthAngle: this.maxAzimuthAngle,
      minDistance: this.minDistance,
      maxDistance: this.maxDistance,
      minZoom: this.minZoom,
      maxZoom: this.maxZoom,
    }
  }

  private restoreNativeHardLimits(): void {
    if (!this.nativeHardLimits) return
    const limits = this.nativeHardLimits
    this.minPolarAngle = limits.minPolarAngle
    this.maxPolarAngle = limits.maxPolarAngle
    this.minAzimuthAngle = limits.minAzimuthAngle
    this.maxAzimuthAngle = limits.maxAzimuthAngle
    this.minDistance = limits.minDistance
    this.maxDistance = limits.maxDistance
    this.minZoom = limits.minZoom
    this.maxZoom = limits.maxZoom
  }

  private applyConfiguredHardLimits(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera
  ): void {
    if (!this.elasticLimits) return
    const { polar, azimuth, distance, zoom } = this.elasticLimits
    if (polar) {
      this.minPolarAngle = polar.hardMin
      this.maxPolarAngle = polar.hardMax
    }
    if (azimuth) {
      this.minAzimuthAngle = azimuth.hardMin
      this.maxAzimuthAngle = azimuth.hardMax
    }
    if (distance) {
      this.minDistance = camera instanceof THREE.PerspectiveCamera ? distance.hardMin : 0
      this.maxDistance = camera instanceof THREE.PerspectiveCamera ? distance.hardMax : Infinity
    }
    if (zoom) {
      this.minZoom = zoom.hardMin
      this.maxZoom = zoom.hardMax
    }
  }

  private applyElasticCorrection(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    before: OrbitElasticSnapshot,
    deltaTime: number
  ): boolean {
    if (!this.elasticLimits) return false

    const after = this.readElasticSnapshot(camera)
    const inputActive = this.interactionActive || this.wheelIdleRemaining > 0
    const polar = resolveElasticValue(
      before.polar,
      after.polar,
      this.elasticLimits.polar,
      this.elasticLimits,
      inputActive,
      deltaTime
    )
    const azimuth = resolveElasticValue(
      before.azimuth,
      after.azimuth,
      this.elasticLimits.azimuth,
      this.elasticLimits,
      inputActive,
      deltaTime
    )
    const distance =
      camera instanceof THREE.PerspectiveCamera
        ? resolveElasticValue(
            before.distance,
            after.distance,
            this.elasticLimits.distance,
            this.elasticLimits,
            inputActive,
            deltaTime
          )
        : after.distance
    const zoom =
      camera instanceof THREE.OrthographicCamera
        ? resolveElasticValue(
            before.zoom,
            after.zoom,
            this.elasticLimits.zoom,
            this.elasticLimits,
            inputActive,
            deltaTime
          )
        : after.zoom

    const poseChanged =
      Math.abs(polar - after.polar) > Number.EPSILON ||
      Math.abs(azimuth - after.azimuth) > Number.EPSILON ||
      Math.abs(distance - after.distance) > Number.EPSILON
    const zoomChanged = Math.abs(zoom - after.zoom) > Number.EPSILON
    if (!poseChanged && !zoomChanged) return false

    if (poseChanged) {
      this.elasticSpherical.set(distance, polar, azimuth)
      this.elasticOffset.setFromSpherical(this.elasticSpherical)
      this.elasticQuaternion.setFromUnitVectors(camera.up, ORBIT_SPHERICAL_UP).invert()
      this.elasticOffset.applyQuaternion(this.elasticQuaternion)
      camera.position.copy(this.target).add(this.elasticOffset)
      camera.lookAt(this.target)
    }
    if (zoomChanged && camera instanceof THREE.OrthographicCamera) {
      camera.zoom = zoom
      camera.updateProjectionMatrix()
    }
    return true
  }

  private readElasticSnapshot(camera: THREE.Object3D): OrbitElasticSnapshot {
    this.elasticOffset.copy(camera.position).sub(this.target)
    this.elasticQuaternion.setFromUnitVectors(camera.up, ORBIT_SPHERICAL_UP)
    this.elasticOffset.applyQuaternion(this.elasticQuaternion)
    this.elasticSpherical.setFromVector3(this.elasticOffset)
    return {
      polar: this.elasticSpherical.phi,
      azimuth: this.elasticSpherical.theta,
      distance: this.elasticSpherical.radius,
      zoom: camera instanceof THREE.OrthographicCamera ? camera.zoom : 1,
    }
  }

  private resolveFrameDelta(deltaTime?: number | null): number {
    if (deltaTime === undefined || deltaTime === null) return 1 / 60
    return Number.isFinite(deltaTime) ? Math.max(0, deltaTime) : 0
  }

  private syncFromRig(): ReturnType<typeof syncViewportCameraFromRig> {
    if (!this.services) {
      return this.object as ReturnType<typeof syncViewportCameraFromRig>
    }

    const camera = syncViewportCameraFromRig(this.services)
    camera.up.copy(ORBIT_WORLD_UP)
    camera.lookAt(this.services.rig.target)
    if (this.object !== camera) {
      this.object = camera
    }
    return camera
  }
}
